import React, { useEffect } from 'react';
import { OneColumnLayout, TwoColumnLayout } from '../layouts';
import { useViewport } from '../../hooks/useViewport';
import { MobileLoadingGrid } from './components/MobileLoadingGrid/MobileLoadingGrid';
import { LoadingGrid } from './components/LoadingGrid/LoadingGrid';
import { SideContent } from './components/LoadingGrid/SideContent';
import { PRODUCTS_BY_CATEGORY } from '../../consts';
import {
  useCreateSitelessMetasite,
  useInstallStoresApp,
  useAttachTemplate,
  useSetupModalystProductsByCategory,
  useUpdateTheme,
  useInstallApps,
  useDeleteAllProducts,
} from '../../features/templateSelection/api';
import { withRetry } from '../../utils/retry';

interface LoadingGridWrapperProps {
  category: string;
}

export function LoadingGridWrapper({ category }: LoadingGridWrapperProps) {
  const { isMobile } = useViewport();
  const { mutateAsync: createSitelessMetasite } = useCreateSitelessMetasite();
  const { mutateAsync: installStoresApp } = useInstallStoresApp();
  const { mutateAsync: attachTemplate } = useAttachTemplate();
  const { mutateAsync: installApps } = useInstallApps();
  const { mutateAsync: deleteAllProducts } = useDeleteAllProducts();
  const { mutateAsync: setupProductsByCategory } = useSetupModalystProductsByCategory();
  const { mutateAsync: updateTheme } = useUpdateTheme();

  const products = PRODUCTS_BY_CATEGORY[category];

  useEffect(() => {
    const initializeSite = async () => {
      if (!category) {
        return;
      }

      try {
        const params = new URLSearchParams(window.location.search);
        const categoryParam = params.get('categoryId') || '';
        // Remove email suffix if present (e.g., "pt.rts.kidsBabies^email@test.com" -> "pt.rts.kidsBabies")
        const [rawCategoryId] = categoryParam.split('^');
        // Extract lang from categoryId if format is "lang.rts.category" or "lang.none" (e.g., "pt.rts.kidsBabies", "pt.none")
        const langMatch = rawCategoryId.match(/^([a-z]{2})\.(?:rts\.|none)/i);
        const lang = langMatch ? langMatch[1].toLowerCase() : undefined;

        // Step 1: Create siteless metasite
        const metaSiteId = await createSitelessMetasite();

        // Step 2: Install stores app (v3)
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await withRetry(() => installStoresApp({ metaSiteId }), { maxRetries: 5, retryDelay: 2000 });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        // Step 3: Attach template to the metasite
        await attachTemplate({ metaSiteId, category, lang });

        await new Promise((resolve) => setTimeout(resolve, 2000));
        await deleteAllProducts();

        await installApps({ metaSiteId });
        await new Promise((resolve) => setTimeout(resolve, 2000));

        await setupProductsByCategory({ category }); // test
        await updateTheme();
      } catch (error) {
        console.error('Failed to create site:', error);
      }
    };

    initializeSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  if (isMobile) {
    return <OneColumnLayout content={<MobileLoadingGrid products={products} />} />;
  }

  return (
    <TwoColumnLayout
      mainContent={<LoadingGrid products={products} />}
      sideContent={<SideContent products={products} />}
      mainContentSpan={6}
    />
  );
}
